import { Form, PageHeader } from 'antd'
import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'

import Error from '../../components/Error'
import FormBlock from '../../components/forms/FormBlock'
import FormWrap from '../../components/forms/FormWrap'
import Loader from '../../components/Loader'
import {
  ProductsMediaForm,
  ProductsOptionsForm,
  ProductsPublicationForm,
} from '../../forms/Products'
import { getProductDetails, updateProduct } from '../../network/Products'
import { Details } from './CreateMpProduct'
import { getProductCategoriesBasic } from '../../network/API'
import { getProductTags } from '../../network/Tags'
import { useAuth } from '../../hooks/useAuth'
import { getCurrency } from '../../network/Stores'
import { getCurrencySymbol } from '../../utils/currencyUtils'
import { createUrlWithFiltersFromCurrentURL } from '../../utils/filterUtils'

const EditMpProduct = ({ history }) => {
  let { productId } = useParams()
  const { storeId } = useParams()
  const { user } = useAuth()
  
  // Create a new form instance when productId changes
  const [form] = Form.useForm()
  
  // Reset form when productId changes (when navigating to different product)
  useEffect(() => {
    form.resetFields()
  }, [productId])

  const productDetails = useQuery(
    [
      'getProductDetails',
      {
        id: productId,
      },
    ],
    getProductDetails,
    {
      onSuccess: (data) => {
        if (data.details && data.details[0]) {
        }
      },
      refetchOnWindowFocus: false,
    }
  )
  
  // Reset and set form values when product data changes
  useEffect(() => {
    if (productDetails.data) {
      form.resetFields()
      form.setFieldsValue(productDetails.data)
    }
  }, [productDetails.data])

  const [currencySymbol, setCurrencySymbol] = useState('')

  useEffect(() => {
    const fetchCurrency = async () => {
      const currencyValue = await getCurrency(storeId)
      const symbol = getCurrencySymbol(currencyValue.currency)
      setCurrencySymbol(symbol)
    }

    fetchCurrency()
  }, [storeId])

  const ProductsDescriptionForm = {
    title: 'Description',
    key: [],
    sections: [
      {
        columns: 2,
        fields: [
          {
            label: 'Product Name',
            placeholder: 'Name',
            isRequired: true,
            key: ['name', 'en'],
            pos: {
              col: 1,
              row: 1,
              size: 1,
            },
          },
          // Display only if the store is not a franchise
          ...(!user.franchiseStore
            ? [
                {
                  label: 'Product Name (中文)',
                  placeholder: 'Chinese name',
                  isRequired: true,
                  key: ['name', 'zh'],
                  pos: {
                    col: 1,
                    row: 2,
                    size: 1,
                  },
                },
              ]
            : []),
          {
            label: 'Type',
            type: 'select',
            isRequired: true,
            key: ['productTypeFixed'],
            options: [
              { label: 'Fixed design', key: 'fixed_design' },
              { label: 'Fixed SKU', key: 'fixed_sku' },
            ],
            pos: {
              col: 1,
              row: 3,
              size: 1,
            },
          },
          {
            label: 'SKU',
            placeholder: 'Ref ID',
            isRequired: true,
            key: ['sku'],
            pos: {
              col: 1,
              row: 4,
              size: 1,
            },
          },
          {
            label: 'Collection',
            type: 'select',
            isRequired: true,
            key: ['category'],
            requestOption: async ({ queryKey }) => {
              const currentStore = user.currentStore
              const filter = { store: currentStore }

              return getProductCategoriesBasic({
                queryKey: [
                  'getProductCategoriesBasic',
                  {
                    filter,
                    sort: {},
                    range: {},
                    tabFilter: {},
                  },
                ],
              })
            },

            pos: {
              col: 1,
              row: 5,
              size: 1,
            },
          },
          {
            label: 'Tag',
            type: 'select',
            key: ['tag'],
            isRequired: false,
            requestOption: async ({ queryKey }) => {
              const currentStore = user.currentStore
              const filter = { store: currentStore }

              return getProductTags({
                queryKey: [
                  'getProductTags',
                  {
                    filter,
                    sort: {},
                    range: {},
                    tabFilter: {},
                  },
                ],
              })
            },
            pos: {
              col: 1,
              row: 6,
              size: 1,
            },
          },
          {
            label: `Retail price (${currencySymbol})`,
            isRequired: true,
            key: ['retailPrice'],
            pos: {
              col: 1,
              row: 7,
              size: 1,
            },
          },
          {
            label: 'Delivery time (days)',
            isRequired: true,
            key: ['deliveryTime'],
            pos: {
              col: 1,
              row: 8,
              size: 1,
            },
          },
          {
            label: 'Description',
            type: 'text',
            isRequired: true,
            key: ['description', 'en'],
            pos: {
              col: 1,
              row: 9,
              size: 1,
            },
          },
          // Display only if the store is not a franchise
          ...(!user.franchiseStore
            ? [
                {
                  label: 'Description (中文)',
                  type: 'text',
                  isRequired: true,
                  key: ['description', 'zh'],
                  pos: {
                    col: 1,
                    row: 10,
                    size: 1,
                  },
                },
                {
                  label: 'Gender',
                  type: 'basicRadio',
                  isRequired: false,
                  key: ['gender'],
                  options: [
                    { label: 'Male', key: 'male' },
                    { label: 'Female', key: 'female' },
                    { label: 'Both', key: 'both' },
                  ],
                  pos: {
                    col: 1,
                    row: 11,
                    size: 1,
                  },
                },
                {
                  label: 'Reference',
                  isRequired: false,
                  key: ['reference'],
                  pos: {
                    col: 1,
                    row: 12,
                    size: 1,
                  },
                },
                {
                  label: 'Show for mix-and-match',
                  type: 'checkbox',
                  isRequired: false,
                  key: ['mixAndMatch'],
                  default: false,
                  pos: {
                    col: 1,
                    row: 13,
                    size: 1,
                  },
                },
              ]
            : []),
        ],
      },
    ],
  }

  return (
    <>
      <PageHeader
        className="site-page-header"
        title={'Edit product'}
        onBack={() => {
          const url = createUrlWithFiltersFromCurrentURL('/mp-products')
          history.push(url)
        }}
        style={{ backgroundColor: '#fff' }}
      />
      {productDetails.isError ? (
        <Error retry={() => productDetails.refetch()} />
      ) : productDetails.isLoading ? (
        <Loader />
      ) : (
        <FormWrap
          key={productId}
          id={productId}
          form={form}
          title="update_product"
          request={updateProduct}
          cancelText={'Cancel'}
          okText={'Save'}
          data={productDetails.data}
          onSuccess={() => {
            const url = createUrlWithFiltersFromCurrentURL('/mp-products')
            history.push(url)
          }}
          onCancel={() => {
            const url = createUrlWithFiltersFromCurrentURL('/mp-products')
            history.push(url)
          }}
          cleanBeforeSending={(oldData) => {
            oldData.store = storeId
          
            
            // Clean up null/undefined values from details
            if (oldData.details && Array.isArray(oldData.details)) {
              oldData.details = oldData.details.map(detail => {
                const cleanedDetail = {}
                
                if (detail._id) {
                  cleanedDetail._id = detail._id
                }
                
                // For each product type (shoes, vest, pant, etc.)
                Object.keys(detail).forEach(productType => {
                  if (productType === '_id') return
                  
                  if (detail[productType] && typeof detail[productType] === 'object') {
                    const cleanedProductData = {}
                    
                    // Remove null/undefined values from product data
                    Object.keys(detail[productType]).forEach(key => {
                      const value = detail[productType][key]
                      if (value !== null && value !== undefined && value !== '') {
                        cleanedProductData[key] = value
                      }
                    })
                    
                    // Special handling for shoes: TODO: Need to check for next version the requirement for each product
                    if (productType === 'shoes' && Object.keys(cleanedProductData).length > 0) {
                      const requiredDefaults = {
                        soleConfection: 'blake',
                        soleMaterial: 'leather',
                        sole: 'welted',
                        leatherSoleOption: 'plain',
                        topReinforcement: 'no',
                        frontTop: 'no',
                        fur: 'no',
                        heels: 'no'
                      }
                      
                      // Apply defaults only for fields that are completely missing
                      Object.keys(requiredDefaults).forEach(field => {
                        if (!cleanedProductData.hasOwnProperty(field)) {
                          cleanedProductData[field] = requiredDefaults[field]
                        }
                      })
                    }
                    
                    // Only include product type if it has data
                    if (Object.keys(cleanedProductData).length > 0) {
                      cleanedDetail[productType] = cleanedProductData
                    }
                  }
                })
                
                return cleanedDetail
              })
            }  
            
            if (oldData.mainPicture) {
              let main = oldData.mainPicture.fileList
                ? oldData.mainPicture?.fileList
                : oldData.mainPicture

              if (!Array.isArray(main)) {
                main = [main]
              }
              const newPic = main.map((file) =>
                file.response ? file.response : file
              )

              oldData.mainPicture = newPic.length ? newPic[0] : {}
            } else {
              oldData.mainPicture = {}
            }

            if (oldData.otherPictures) {
              const otherPic = oldData.otherPictures.fileList
                ? oldData.otherPictures?.fileList
                : oldData.otherPictures
              oldData.otherPictures = otherPic.map((file) =>
                file.response ? file.response : file
              )
            } else {
              oldData.otherPictures = []
            }

            return oldData
          }}
          invalidate={['getProducts', 'getProductDetails']}
        >
          <div className="section-from-vertical">
            <FormBlock form={form} formTemplate={ProductsDescriptionForm} />
            <Details form={form} />
            {!user.franchiseStore && (
              <FormBlock form={form} formTemplate={ProductsMediaForm} />
            )}
            <FormBlock form={form} formTemplate={ProductsOptionsForm} />
            <FormBlock.Basic formTemplate={ProductsPublicationForm} />
          </div>
        </FormWrap>
      )}
    </>
  )
}

export default EditMpProduct
